import React, { useState } from 'react';
import styled from 'styled-components';
import { colors } from '@/constants/colors';

/**
 * One payment a customer says they made, beside the bill it is about.
 *
 * Ray, 2026-09-01: *"when click it should show modal of the payments details +
 * the proof of payment. then an action approve and reject."*
 *
 * ─── THE AMOUNT IS THE OPERATOR'S TO CHANGE ───
 * 🔴 It opens at what the CUSTOMER claimed, and the operator types what
 * actually arrived. A screenshot showing ₱500 against a ₱999 bill is a PARTIAL
 * payment, not a wrong one — and recording the claim instead of the reality is
 * how an invoice ends up marked paid on money nobody received.
 *
 * ─── THE SCREENSHOT IS THE DECISION ───
 * Shown full width, openable, beside the numbers. An operator is comparing an
 * amount and a reference on an image to the ones on the form; a thumbnail
 * cannot be read.
 */
const peso = (n: number) => `₱${Number(n ?? 0).toLocaleString()}`;

interface Props {
   customerName: string;
   invoiceNumber: string;
   outstanding: number;
   proof: {
      id: string;
      amount: number;
      reference: string;
      proofUrl: string;
      note: string;
      createdAt: string | null;
   };
   onCancel: () => void;
   onApprove: (amount: number) => void;
   onReject: (reason: string) => void;
   busy?: boolean;
}

const IspProofReview: React.FC<Props> = ({
   customerName,
   invoiceNumber,
   outstanding,
   proof,
   onCancel,
   onApprove,
   onReject,
   busy,
}) => {
   const [amount, setAmount] = useState(String(proof.amount));
   const [rejecting, setRejecting] = useState(false);
   const [reason, setReason] = useState('');

   const n = Number(amount);
   const valid = Number.isFinite(n) && n > 0;
   const partial = valid && n < outstanding;

   return (
      <Wrap>
         {/* 🔴 SHORT ON PURPOSE. Ray, 2026-09-03: *"make the modal height
             short… you dont need to large the photo preview since it has
             button open full size."* The screenshot was 340px of the dialog
             and pushed the decision — the amount and the two buttons — off the
             bottom on a laptop. It is a thumbnail beside the facts now; the
             full picture is one click away and opens where a picture belongs. */}
         <Top>
         <Rows>
            <Row>
               <K>Customer</K>
               <V>{customerName}</V>
            </Row>
            <Row>
               <K>Bill</K>
               <V>
                  {invoiceNumber} · {peso(outstanding)} outstanding
               </V>
            </Row>
            <Row>
               <K>They sent</K>
               <V>{peso(proof.amount)}</V>
            </Row>
            <Row>
               <K>Reference</K>
               <V>{proof.reference || <Muted>None given</Muted>}</V>
            </Row>
            {proof.note ? (
               <Row>
                  <K>Note</K>
                  <V>{proof.note}</V>
               </Row>
            ) : null}
         </Rows>

         {proof.proofUrl ? (
            <ProofLink href={proof.proofUrl} target="_blank" rel="noreferrer">
               <ProofImg src={proof.proofUrl} alt="Payment screenshot" />
               <Open>Open full size</Open>
            </ProofLink>
         ) : (
            <NoProof>No screenshot was attached.</NoProof>
         )}
         </Top>

         {rejecting ? (
            <Box>
               <Lbl htmlFor="proof-why">Why are you rejecting it?</Lbl>
               <Input
                  id="proof-why"
                  value={reason}
                  placeholder="e.g. That screenshot is from last month"
                  onChange={(e) => setReason(e.target.value)}
               />
               {/* The customer reads this on their portal. A bare "rejected"
                   ends in a phone call the operator has to take. */}
               <Hint>The customer sees this on their bill page.</Hint>
               <Actions>
                  <Ghost type="button" onClick={() => setRejecting(false)}>
                     Back
                  </Ghost>
                  <Danger
                     type="button"
                     disabled={busy}
                     onClick={() => onReject(reason.trim())}
                  >
                     Reject payment
                  </Danger>
               </Actions>
            </Box>
         ) : (
            <>
               <Box>
                  <Lbl htmlFor="proof-amount">How much actually arrived?</Lbl>
                  {/* type=text with inputMode: a number input eats the decimal
                      point on some keyboards. */}
                  <Num
                     id="proof-amount"
                     type="text"
                     inputMode="decimal"
                     value={amount}
                     onChange={(e) => setAmount(e.target.value)}
                  />
                  <Hint>
                     Opens at what they claimed. Change it to what your wallet
                     shows — that is what gets recorded.
                  </Hint>
                  {partial ? (
                     <Partial>
                        {peso(n)} of {peso(outstanding)} — the rest stays owing,
                        and their bill will still show it.
                     </Partial>
                  ) : null}
               </Box>
               <Actions>
                  <Ghost type="button" onClick={onCancel}>
                     Close
                  </Ghost>
                  <Danger type="button" onClick={() => setRejecting(true)}>
                     Reject
                  </Danger>
                  <Approve
                     type="button"
                     disabled={busy || !valid}
                     onClick={() => onApprove(n)}
                  >
                     Approve {valid ? peso(n) : ''}
                  </Approve>
               </Actions>
            </>
         )}
      </Wrap>
   );
};

const Wrap = styled.div`
   padding: 4px 2px;
`;
/** Facts on the left, the screenshot on the right — half the height. */
const Top = styled.div`
   display: flex;
   align-items: flex-start;
   gap: 14px;
   margin-bottom: 14px;

   @media (max-width: 560px) {
      flex-direction: column;
   }
`;
const Rows = styled.div`
   flex: 1;
   min-width: 0;
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
   padding: 0 12px;
`;
const Row = styled.div`
   display: flex;
   gap: 12px;
   padding: 8px 0;
   font-size: 13px;
   & + & {
      border-top: 1px solid ${colors.GRAY12};
   }
`;
const K = styled.span`
   width: 78px;
   flex: none;
   color: ${colors.GRAY5};
`;
const V = styled.span`
   font-weight: 600;
   overflow-wrap: anywhere;
`;
const Muted = styled.span`
   font-weight: 500;
   color: ${colors.GRAY5};
`;
const ProofLink = styled.a`
   display: block;
   flex: none;
   width: 132px;
   text-decoration: none;
   color: ${colors.PRIMARY};

   @media (max-width: 560px) {
      width: 100%;
   }
`;
const ProofImg = styled.img`
   display: block;
   width: 100%;
   height: 132px;
   object-fit: cover;
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
   background: ${colors.GRAY14};
`;
const Open = styled.div`
   margin-top: 5px;
   font-size: 12px;
   font-weight: 700;
   text-align: center;
`;
const NoProof = styled.div`
   flex: none;
   width: 132px;
   display: flex;
   align-items: center;
   text-align: center;
   font-size: 12px;
   padding: 12px;
   border: 1px dashed ${colors.RED};
   border-radius: 10px;
   font-size: 13px;
   font-weight: 600;
   color: ${colors.RED};
   text-align: center;
`;
const Box = styled.div`
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
   padding: 14px;
`;
const Lbl = styled.label`
   display: block;
   font-size: 13px;
   font-weight: 700;
   margin-bottom: 6px;
`;
const inputBase = `
   font-family: inherit;
   font-size: 14px;
   padding: 10px 12px;
   border-radius: 9px;
   background: transparent;
   color: inherit;
`;
const Input = styled.input`
   ${inputBase}
   width: 100%;
   box-sizing: border-box;
   border: 1px solid ${colors.GRAY12};
`;
const Num = styled.input`
   ${inputBase}
   width: 150px;
   border: 1px solid ${colors.GRAY12};
`;
const Hint = styled.div`
   margin-top: 5px;
   font-size: 12px;
   color: ${colors.GRAY5};
`;
const Partial = styled.div`
   margin-top: 8px;
   font-size: 12px;
   font-weight: 700;
   color: ${colors.PRIMARY};
`;
const Actions = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 8px;
   margin-top: 14px;
   flex-wrap: wrap;
`;
const btn = `
   font-family: inherit;
   font-size: 14px;
   font-weight: 700;
   padding: 10px 16px;
   border-radius: 9px;
   cursor: pointer;
`;
const Ghost = styled.button`
   ${btn}
   border: 1px solid ${colors.GRAY12};
   background: transparent;
   color: inherit;
`;
const Danger = styled.button`
   ${btn}
   border: 1px solid ${colors.RED};
   background: transparent;
   color: ${colors.RED};
`;
const Approve = styled.button`
   ${btn}
   border: none;
   background: ${colors.PRIMARY};
   color: #fff;
`;

export default IspProofReview;
